import type { AnswerSelectDto, QuestionSelectDto, SurveyStatus } from '@@types/api.types';
import { AssessmentProgressQuestion } from '@components/pages/assessment-shell/components/assessment-progress-question.component';
import {
  NavigationButtons,
  ShortcutBadge,
} from '@components/pages/assessment-shell/components/assessment-question-shared.component';
import { AnswerAttachmentsInput } from '@components/pages/survey/components/answer-attachments-input.component';
import { errorToast } from '@hooks/toast.hook';
import { useAutoSaveAnswer } from '@hooks/use-auto-save-answer.hook';
import { useGetSurvey } from '@queries/survey.query';
import { useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { getQuestionIndexInSurvey } from '@utils/survey.util';
import { useCallback, useState } from 'react';

type QuestionComponentProps = {
  answers?: AnswerSelectDto[];
  handleNextQuestion: (skipToQuestionId?: string) => void;
  handlePreviousQuestion: () => void;
  previousQuestion?: QuestionSelectDto;
  question: QuestionSelectDto;
  questionOptionsValues: string[];
  sectionId?: string;
  surveyId?: string;
};

export { NavigationButtons, ShortcutBadge };

export const QuestionComponent: React.FC<QuestionComponentProps> = (props) => {
  const {
    answers = [],
    handleNextQuestion,
    handlePreviousQuestion,
    previousQuestion,
    question,
    questionOptionsValues,
    sectionId,
    surveyId,
  } = props;

  const { data: survey } = useGetSurvey(surveyId);
  const disableInputs = survey?.status !== ('DRAFT' as SurveyStatus);
  const answer = answers.find((item) => item.questionId === question.id && item.sectionId === sectionId);
  const questionAttachmentKey = surveyId && sectionId ? `${surveyId}:${sectionId}:${question.id}` : undefined;
  const bufferedAttachmentCount = useSurveyAttachmentsStore((state) => {
    if (!questionAttachmentKey) {
      return 0;
    }

    return state.bufferedByQuestion[questionAttachmentKey]?.length ?? 0;
  });
  const hasPendingBufferWithoutAnswer = Boolean(!answer?.id && bufferedAttachmentCount > 0);
  const { details, flush, isPending, onDetailsBlur, onDetailsChange, onOptionChange, selectedOption } =
    useAutoSaveAnswer({
      answer,
      disableInputs,
      questionId: question.id,
      sectionId,
      surveyId,
    });
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isNextClicked, setIsNextClicked] = useState(false);
  const [isPreviousClicked, setIsPreviousClicked] = useState(false);
  const globalIndexInSurvey = getQuestionIndexInSurvey({
    questionId: question.id,
    sectionId,
    snapshot: survey?.snapshot,
  });

  const showPendingBufferWarning = useCallback(() => {
    errorToast({ description: 'Answer should be submitted in order to save attachment.' });
  }, []);

  const handleNext = useCallback(async () => {
    if (hasPendingBufferWithoutAnswer) {
      showPendingBufferWarning();
      return;
    }

    setIsNextClicked(true);
    try {
      const skipToQuestionId = await flush();
      handleNextQuestion(skipToQuestionId);
    } finally {
      setIsNextClicked(false);
    }
  }, [flush, handleNextQuestion, hasPendingBufferWithoutAnswer, showPendingBufferWarning]);

  const handlePrevious = useCallback(async () => {
    if (hasPendingBufferWithoutAnswer) {
      showPendingBufferWarning();
      return;
    }

    setIsPreviousClicked(true);
    try {
      await flush();
      handlePreviousQuestion();
    } finally {
      setIsPreviousClicked(false);
    }
  }, [flush, handlePreviousQuestion, hasPendingBufferWithoutAnswer, showPendingBufferWarning]);

  return (
    <AssessmentProgressQuestion
      afterDetailsSlot={
        <AnswerAttachmentsInput
          answerId={answer?.id}
          canEdit={!disableInputs}
          onModalOpenChange={setIsAttachmentModalOpen}
          questionAttachmentKey={questionAttachmentKey}
        />
      }
      description={question.description}
      details={details}
      detailsTooltip={
        <>
          Provide as much relevant information as possible to support your answer. Do not include sensitive intellectual
          property or any information that is classified or would violate information security regulations. Ensure your
          response is detailed enough for an expert, government agency, or approved organization to assess its validity
          and credibility.
        </>
      }
      disabled={disableInputs}
      isKeyboardNavigationDisabled={isAttachmentModalOpen}
      isNextLoading={isNextClicked && isPending}
      isPreviousLoading={isPreviousClicked && isPending}
      onDetailsBlur={onDetailsBlur}
      onDetailsChange={onDetailsChange}
      onNext={handleNext}
      onOptionChange={(option) => {
        if (!disableInputs) {
          onOptionChange(option);
        }
      }}
      onPrevious={handlePrevious}
      optionValues={questionOptionsValues}
      previousQuestion={Boolean(previousQuestion)}
      prompt={question.prompt}
      questionNumber={globalIndexInSurvey + 1}
      selectedOption={selectedOption}
    />
  );
};
